/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Fragment, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { ShareView } from 'components';
import { NameRenderer } from 'components/EntityTable/CellRenderer';
import { EmptyState, Spinner } from 'primitives';
import { DiagramShapes, Folder, SadFace } from 'icons';
import { diagramExtensionStore, projectStore } from 'stores';
import hasAccess, { actions } from 'utils/user-access';
import { getAuthorName, getEntityIcon, getEntityLink, getEntityName, getSortingPositionByEntity } from 'utils/helpers';
import { dedicatedModesStore } from 'App/Pages/Diagram/stores';
import { IdpApplicationCreationModal } from 'App/Pages/Project/IdpApplication';
import { LandscapeView } from 'features/landscape-view';
import { FolderExplorer, useFetchReadme } from 'features/folder-exploring';
import { useRunOnceDuringRender } from 'hooks';

import FolderHeader from './FolderHeader';
import ActionButton from './ActionButton';
import getBatchActions from './BatchActions';
import EntityAction from './EntityAction';
import { getFileDate } from './utils';
import * as Styled from './Shared.styled';

const FolderPage = () => {
  useRunOnceDuringRender(() => {
    projectStore.reset();
  });

  // @ts-expect-error TS2339
  const { slug } = useParams();
  const { project, folder, loading: isStoreLoading, entities } = projectStore;

  const { currentReadme, isReadmePresent, isReadmeLoading } = useFetchReadme({
    entities: isStoreLoading ? null : entities
  });

  const { batchActions, targetSelector } = getBatchActions();

  useEffect(() => {
    // @ts-expect-error TS2554
    projectStore.init('folder', slug.split('--')[0]);

    return () => projectStore.reset();
  }, [slug]);

  return (
    <Fragment>
      <FolderHeader />

      {isStoreLoading || isReadmeLoading ? (
        <Spinner fullHeight />
      ) : folder.id ? (
        <>
          <Styled.Container>
            <FolderExplorer
              action={({ tabIndex }) => (
                <>
                  <LandscapeView project={project} tabIndex={tabIndex} />
                  <ActionButton
                    label="Create new"
                    variant="primary"
                    onUpload={projectStore.uploadFiles}
                    isReadmePresent={isReadmePresent}
                  />
                </>
              )}
              currentReadme={currentReadme}
              canEditReadme={hasAccess(project, actions.MODIFY_README)}
              batchActions={batchActions}
              emptyState={
                // @ts-expect-error TS2741
                <EmptyState
                  icon={<DiagramShapes />}
                  title="This folder doesn't have any content yet."
                  description={`Everyone with access to the project "${project.name}" also has access to the content in this folder.`}
                  action={
                    <ActionButton
                      label="Create new"
                      variant="primary"
                      onUpload={projectStore.uploadFiles}
                      isReadmePresent={isReadmePresent}
                    />
                  }
                />
              }
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  renderer: NameRenderer,
                  noPadding: true,
                  sortable: true
                },
                {
                  key: 'creator',
                  header: 'Creator',
                  width: '150px',
                  sortable: true
                },
                {
                  key: 'lastChanged',
                  header: 'Last changed',
                  width: '200px',
                  sortable: true
                },
                {
                  key: 'entityAction',
                  header: '',
                  width: '50px',
                  noPadding: true,
                  renderer: (entity) => <EntityAction entity={entity} />
                }
              ]}
              onDrop={hasAccess(project, actions.MODIFY_DIAGRAM) && projectStore.uploadFiles}
              title={
                <Styled.Title data-test="folder-name">
                  <Folder width="24" height="24" />
                  {folder.name}
                </Styled.Title>
              }
              rows={entities.map((entity) => ({
                __sortingPosition: getSortingPositionByEntity(entity),
                id: entity.id,
                name: {
                  name: getEntityName(entity),
                  icon: getEntityIcon(entity),
                  link: getEntityLink(entity),
                  onClick: () => {
                    if (entity.type === 'BPMN') {
                      dedicatedModesStore.resetViewModeIndexForPlay();
                    }
                  }
                },
                creator: getAuthorName(entity),
                lastChanged: getFileDate(entity),
                entityAction: entity
              }))}
            />

            {diagramExtensionStore.state.isShareModalVisible && (
              <ShareView diagram={diagramExtensionStore.state.diagram} onClose={diagramExtensionStore.hideShareModal} />
            )}

            {targetSelector}
          </Styled.Container>
          <IdpApplicationCreationModal />
        </>
      ) : (
        // @ts-expect-error TS2739
        <EmptyState
          title="Folder not found!"
          description="Sorry, your folder might have been deleted."
          icon={<SadFace width="48" height="48" />}
        />
      )}
    </Fragment>
  );
};

export default observer(FolderPage);
